/* ─────────────────────────────────────────────────────────────
   The building's cameras.

   Footage is served from public/cameras/ rather than bundled: it is
   a megabyte or two a clip, and a kiosk should not download every
   camera before it can show the welcome screen. Each clip is
   <key>.mp4 (H.264 at 1280x720, silent, faststart: it plays everywhere,
   iOS included, so there is no WebM copy) with <key>.jpg as its
   poster frame.

   Every clip is a locked-off shot: each passed a camera drift check and
   a stillness check, then two independent visual reviews (CREDITS.md).
   A few are sped up, which the client is happy with. A
   wide-angle lens and a security sensor's grade are added, so a tile
   reads as a mounted camera rather than someone filming. `ir: true`
   marks a camera graded as infrared night vision.

   `still` is a small bundled frame of the same clip, shown if the clip
   is missing or will not play, so a camera never renders as a black
   box. Sources and licences are in CREDITS.md.

   `resident: true` marks the amenity cameras residents may watch from
   their own screen. Corridors, stairs and back of house stay staff-only.
   ───────────────────────────────────────────────────────────── */

import LobbyStill from '../media/images/cameras/lobby.jpg';
import GardenLobbyStill from '../media/images/cameras/garden-lobby.jpg';
import SpiralStairStill from '../media/images/cameras/spiral-stair.jpg';
import EntranceStill from '../media/images/cameras/entrance.jpg';
import CafeStill from '../media/images/cameras/cafe.jpg';
import LaundryStill from '../media/images/cameras/laundry.jpg';
import PoolStill from '../media/images/cameras/pool.jpg';
import LapPoolStill from '../media/images/cameras/lap-pool.jpg';
import LoungeStill from '../media/images/cameras/lounge.jpg';
import ConferenceStill from '../media/images/cameras/conference.jpg';
import LibraryStill from '../media/images/cameras/library.jpg';
import BarStill from '../media/images/cameras/bar.jpg';
import RooftopStill from '../media/images/cameras/rooftop.jpg';
import Corridor9Still from '../media/images/cameras/corridor-9.jpg';
import Corridor12Still from '../media/images/cameras/corridor-12.jpg';
import ElevatorLobbyStill from '../media/images/cameras/elevator-lobby.jpg';
import PackageRoomStill from '../media/images/cameras/package-room.jpg';
import ValetLaundryStill from '../media/images/cameras/valet-laundry.jpg';
import GarageStill from '../media/images/cameras/garage.jpg';
import Garage2Still from '../media/images/cameras/garage-2.jpg';
import MachineRoomStill from '../media/images/cameras/machine-room.jpg';

const base = `${process.env.PUBLIC_URL || ''}/cameras`;

export const clip = key => ({
    mp4: `${base}/${key}.mp4`,
    webm: null,
    poster: `${base}/${key}.jpg`
});

export const CAMERA_GROUPS = ['All', 'Lobby', 'Amenities', 'Floors', 'Back of house'];

export const CAMERAS = [
    { id: 'cam-01', name: 'Main lobby', location: 'Level 1', group: 'Lobby', key: 'lobby', still: LobbyStill, ptz: true, resident: true, ir: false },
    { id: 'cam-20', name: 'Garden lobby', location: 'Level 1', group: 'Lobby', key: 'garden-lobby', still: GardenLobbyStill, ptz: false, resident: true, ir: false },
    { id: 'cam-21', name: 'Lobby spiral stair', location: 'Level 2', group: 'Lobby', key: 'spiral-stair', still: SpiralStairStill, ptz: false, resident: true, ir: false },
    { id: 'cam-25', name: 'Main entrance drop-off', location: 'Level 1', group: 'Lobby', key: 'entrance', still: EntranceStill, ptz: true, resident: false, ir: false },
    { id: 'cam-06', name: 'Lobby café', location: 'Level 1', group: 'Amenities', key: 'cafe', still: CafeStill, ptz: false, resident: true, ir: false },
    { id: 'cam-18', name: 'Laundry room', location: 'Level 2', group: 'Amenities', key: 'laundry', still: LaundryStill, ptz: false, resident: true, ir: false },
    { id: 'cam-07', name: 'Pool deck', location: 'Level 7', group: 'Amenities', key: 'pool', still: PoolStill, ptz: true, resident: true, ir: false },
    { id: 'cam-26', name: 'Indoor lap pool', location: 'Level 7', group: 'Amenities', key: 'lap-pool', still: LapPoolStill, ptz: false, resident: true, ir: false },
    { id: 'cam-03', name: 'Co-working lounge', location: 'Level 8', group: 'Amenities', key: 'lounge', still: LoungeStill, ptz: false, resident: true, ir: false },
    { id: 'cam-22', name: 'Conference room', location: 'Level 8', group: 'Amenities', key: 'conference', still: ConferenceStill, ptz: false, resident: true, ir: false },
    { id: 'cam-27', name: 'Library lounge', location: 'Level 8', group: 'Amenities', key: 'library', still: LibraryStill, ptz: false, resident: true, ir: false },
    { id: 'cam-28', name: 'Club lounge bar', location: 'Level 8', group: 'Amenities', key: 'bar', still: BarStill, ptz: false, resident: true, ir: false },
    { id: 'cam-23', name: 'Rooftop pool', location: 'Roof', group: 'Amenities', key: 'rooftop', still: RooftopStill, ptz: true, resident: true, ir: false },
    { id: 'cam-19', name: 'Corridor, floor 9', location: 'Level 9', group: 'Floors', key: 'corridor-9', still: Corridor9Still, ptz: false, resident: false, ir: false },
    { id: 'cam-08', name: 'Corridor, floor 12', location: 'Level 12', group: 'Floors', key: 'corridor-12', still: Corridor12Still, ptz: false, resident: false, ir: false },
    { id: 'cam-04', name: 'Elevator lobby, floor 18', location: 'Level 18', group: 'Floors', key: 'elevator-lobby', still: ElevatorLobbyStill, ptz: false, resident: false, ir: false },
    { id: 'cam-02', name: 'Package room', location: 'Level 1', group: 'Back of house', key: 'package-room', still: PackageRoomStill, ptz: false, resident: false, ir: false },
    { id: 'cam-16', name: 'Valet laundry', location: 'Level 2, service core', group: 'Back of house', key: 'valet-laundry', still: ValetLaundryStill, ptz: false, resident: false, ir: false },
    { id: 'cam-12', name: 'Garage P1', location: 'Parking level 1', group: 'Back of house', key: 'garage', still: GarageStill, ptz: false, resident: false, ir: false },
    { id: 'cam-17', name: 'Garage P2', location: 'Parking level 2', group: 'Back of house', key: 'garage-2', still: Garage2Still, ptz: false, resident: false, ir: true },
    { id: 'cam-14', name: 'Elevator machine room', location: 'Roof', group: 'Back of house', key: 'machine-room', still: MachineRoomStill, ptz: false, resident: false, ir: true }
];

export const cameraById = id => CAMERAS.find(camera => camera.id === id);
